import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface StreetViewPopupProps {
  open: boolean;
  onClose: () => void;
  location: { lat: number, lng: number } | string | null;
  address?: string;
}

const StreetViewPopup: React.FC<StreetViewPopupProps> = ({ open, onClose, location, address }) => {
  const { t } = useTranslation();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const theme = useTheme();

  if (!apiKey) {
    console.error("Google Maps API key is missing.");
    return null;
  }

  let content;
  const hasCoordinates = location && typeof location === 'object' && 'lat' in location && 'lng' in location;
  
  if (hasCoordinates) {
    const coords = location as { lat: number, lng: number };
    const embedUrl = `https://www.google.com/maps/embed/v1/streetview?key=${apiKey}&location=${coords.lat},${coords.lng}`;
    content = (
      <iframe
        width="100%"
        height="100%"
        style={{ border: 0 }}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        src={embedUrl}
        title={t('graph.streetView')}
      ></iframe>
    );
  } else {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>{t('graph.noLocation')}</Typography>
      </Box>
    );
  }


  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          height: '30vh',
          width: '35vh',
          position: 'relative',
          overflow: 'hidden'
        }
      }}
    >
      <IconButton
        aria-label={t('common.actions.close')}
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent sx={{ p: 0, height: '100%' }}>
        <Box sx={{ width: '100%', height: '100%' }}>
          {content}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StreetViewPopup;
